import { Typography } from "@mui/material";
import { ReactNode } from "react";
import {
    AccountSubscriptionType,
    ConstructorViewInfoStatus,
    ProjectDetailDto,
} from "../api/psc";

export const SubscriptionTypesDefintions = [
    {
        type: AccountSubscriptionType.STANDARD,
        title: "بنیان",
        color: "#9848FF",
        imageUrl: "images/contain/bonyan.png",
    },
    {
        type: AccountSubscriptionType.ENHANCED,
        title: "رویان",
        color: "#9848FF",
        imageUrl: "images/contain/ruyan.png",
    },
    {
        type: AccountSubscriptionType.ULTIMATE,
        title: "تابان",
        color: "#9848FF",
        imageUrl: "images/contain/taban.png",
    },
    {
        type: AccountSubscriptionType.ULTIMATE_PLUS,
        title: "تابان پلاس",
        color: "#9848FF",
        imageUrl: "images/contain/taban_plus.png",
    },
];

export function parseType(type: number): string {
    if (type === 2) {
        return "پروژه های بازاریابی شده";
    } else {
        return "پروژه های عادی";
    }
}

export function getVisitConstructorErrorOrNull(
    projectDetail: ProjectDetailDto
): string | null {
    if (!!projectDetail?.constructorViewInfo) {
        return null;
    }

    if (!projectDetail.canVisit) {
        return "اشتراک شما قادر به مشاهده این پروژه نیست";
    }

    if (!projectDetail.hasAvailableConstructor) {
        return "سازنده فعال برای این پروژه در این لحظه یافت نشد";
    }

    return null;
}

export function getAccessErrorMessageOrNull(
    status?: ConstructorViewInfoStatus
): string | null {
    if (!status || status === ConstructorViewInfoStatus.NO_ERROR) {
        return null;
    }

    switch (status) {
        case ConstructorViewInfoStatus.NOT_AVAILABLE:
            return "سازنده در این لحظه قابل مشاهده نیست";
        case ConstructorViewInfoStatus.ACCESS_DENIED:
            return "شما اجازه دسترسی به این پروژه را ندارید";
        case ConstructorViewInfoStatus.LIMIT_EXCEED:
            return "شما قادر به مشاهده سازنده بیش از تعداد مجاز روزانه نمی باشید";
        case ConstructorViewInfoStatus.LIMITED_ACCOUNT:
            return "دسترسی شما به مشاهده سازنده محدود شده است، لطفا با پشتیبانی تماس حاصل نمایید";
        case ConstructorViewInfoStatus.ACCOUNT_FROZEN:
            return "حساب کاربری شما فریز شده است لطفا به بخش تنظیمات پروژه ها مراجعه نمایید";
        case ConstructorViewInfoStatus.NO_CONSTRUCTOR:
            return "سازنده ای برای این پروژه یافت نشد";
    }
}

export const SubscriptionPricingPeriods = [
    { months: 3, label: "سه‌ماهه" },
    { months: 6, label: "شش‌ماهه" },
    { months: 12, label: "یک‌ساله" },
] as const;

export type SubscriptionPageInfoType = {
    title: string;
    type: AccountSubscriptionType;
    imageUrl: string;
    totalProjectCount: string;
    titleColor: string;
    exclusiveProjectCount: string;
    description: string;
    definition: string;
    pricesInToman: Record<
        (typeof SubscriptionPricingPeriods)[number]["months"],
        number
    >;
    properties: { icon: string; text: ReactNode }[];
    subscriptionForNavigation: AccountSubscriptionType[];
};

export const SubscriptionTypeProperties = {
    STANDARD: {
        imageUrl: "images/contain/bonyan.png",
        pinUrl: "images/contain/bonyan_pin.png",
    },
    ENHANCED: {
        imageUrl: "images/contain/ruyan.png",
        pinUrl: "images/contain/ruyan_pin.png",
    },
    ULTIMATE: {
        imageUrl: "images/contain/taban.png",
        pinUrl: "images/contain/taban_pin.png",
    },
    ULTIMATE_PLUS: {
        imageUrl: "images/contain/taban_plus.png",
        pinUrl: "images/contain/taban_plus_pin.png",
    },
};

// Total prices in toman from https://persiansaze.com/ (2026-10-19).
export const SubscriptionPageInfo: SubscriptionPageInfoType[] = [
    {
        title: "بنیان",
        type: AccountSubscriptionType.STANDARD,
        pricesInToman: { 3: 9_000_000, 6: 13_500_000, 12: 16_500_000 },
        imageUrl: SubscriptionTypeProperties.STANDARD.imageUrl,
        totalProjectCount: "۱۶٬۵۰۰+ پروژه تا آبان",
        titleColor: "#296fe9",
        exclusiveProjectCount: "ندارد",
        definition: "ارزان‌تر اما هم‌چنان کارا",
        description: `
                            «بنیان»، پایه و اساس هرکاری است و این اشتراک اساس
                            کار برای حضور و فروش بیشتر شما در بازار است. مانند
                            پِی و فونداسیون ساختمان که سنگ‌بنا و ضامن استحکام
                            بناست. هرچند در این اشتراک، به پروژه‌های کمتری
                            دسترسی دارید، اما کیفیت و تعهد ما به شما محکم و مثل
                            همیشه پابرجاست. پنل تبلیغات پیامک، CRM متصل به بانک
                            اطلاعات، کارگاه‌های آموزشی مارکتینگ و فروش و دیگر
                            خدمات ما در این اشتراک سرجای خودشان هستند. اگر تازه
                            می‌خواهید وارد بازار شوید و بودجه‌ی محدودی دارید،
                            این اشتراک برای شما مناسب است. با این اشتراک،
                            تجربه‌ای حرفه‌ای از فروش بسازید.
                       `,
        properties: [
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        متراژ زمین: تا ۳۰۰ متر
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        کاربری: مسکونی، مسکونی تجاری و اداری تجاری
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        زمان دسترسی به پروژه‌ها: بلافاصله پس از گردآوری و
                        به‌روزرسانی
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        بازدید روزانه‌ی پروژه‌ها: ۵۰ پروژه
                    </Typography>
                ),
            },
            {
                icon: "close",
                text: (
                    <Typography color="text.secondary">
                        شهرهای حومه: ندارد
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        پنل تبلیغات و پیامک: بدون شارژ اولیه (شارژ باید جداگانه
                        خریداری شود)
                    </Typography>
                ),
            },
            {
                icon: "close",
                text: (
                    <Typography color="text.secondary">
                        سرشماره‌ی خدماتی برای پنل پیامک: باید خریداری شود
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        سازه‌افزار (تبلیغ در وب‌سایت تبلیغاتی صنعت ساختمان در
                        صفحه‌ی یک گوگل): دارد
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        سی-آر-ام (فرآیندهای فروش): دارد
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        کارگاه‌های آموزشی: ۲ بار (کارگاه/نفر) به مدت ۱۶ ساعت{" "}
                    </Typography>
                ),
            },
        ],
        subscriptionForNavigation: [AccountSubscriptionType.STANDARD],
    },
    {
        title: "رویان",
        type: AccountSubscriptionType.ENHANCED,
        pricesInToman: { 3: 12_000_000, 6: 16_500_000, 12: 21_500_000 },
        imageUrl: SubscriptionTypeProperties.ENHANCED.imageUrl,
        totalProjectCount: "۲۴٬۵۰۰+ پروژه تا آبان",
        titleColor: "#19a99f",
        exclusiveProjectCount: "۷٬۵۰۰+ پروژه",
        definition: "حرفه‌ای و به‌صرفه",
        description: `
                            «رویان»، از روییدن، اشتراک رشد، حرکت و پویایی است
                            چون پروژه‌ها منتظر نمی‌مانند و باید بلافاصله دسترسی
                            داشته باشید تا رشد کنید. ما اطلاعات نمی‌فروشیم؛ ما
                            فرصت‌ها را زنده تحویل می‌دهیم. با اشتراک ویژه‌ی
                            «رویان»، پروژه‌ها بلافاصله پس از انتشار در اختیار
                            شما هستند — بدون تاخیر، کامل و جامع. علاوه بر تمامی
                            امکانات و ویژگی‌های اشتراک قبلی، در اشتراک «رویان»
                            خدمات دیگری مانند سرشماره‌ی خدماتی، شارژ رایگان،
                            کارگاه‌های آموزشی رایگان بیشتر با موضوعات گسترده‌تر
                            و ...، در دستان شما هستند تا راحت‌تر و سریع‌تر رشد
                            کنید. این اشتراک مناسب آنهایی است که نمی‌خواهند از
                            رقبا عقب بمانند. با این اشتراک همیشه یک قدم جلو
                            هستید.
                       `,
        properties: [
            {
                icon: "done",
                text: (
                    <>
                        <Typography color="text.secondary">
                            متراژ زمین:
                        </Typography>
                        <Typography
                            color="text.primary"
                            sx={{ fontWeight: 1000 }}
                        >
                            تا ۵۰۰ متر
                        </Typography>
                    </>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        کاربری: مسکونی، مسکونی تجاری و اداری تجاری
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        زمان دسترسی به پروژه‌ها: بلافاصله پس از گردآوری و
                        به‌روزرسانی
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        بازدید هفتگی پروژه‌ها:
                        <Typography
                            component={"span"}
                            color="text.primary"
                            sx={{ fontWeight: "1000" }}
                        >
                            {" "}
                            ۵۰۰ پروژه
                        </Typography>
                    </Typography>
                ),
            },
            {
                icon: "close",
                text: (
                    <Typography color="text.secondary">
                        شهرهای حومه: ندارد
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        پنل تبلیغات و پیامک: بدون شارژ اولیه (شارژ باید جداگانه
                        خریداری شود)
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        سرشماره‌ی خدماتی برای پنل پیامک: &nbsp;
                        <Typography
                            component={"span"}
                            color="text.primary"
                            sx={{ fontWeight: "1000" }}
                        >
                            ۱۰ روز رایگان در اشتراک یک ساله{" "}
                        </Typography>
                        (بیشتر باید جداگانه خریداری شود)
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        سازه‌افزار (تبلیغ در وب‌سایت تبلیغاتی صنعت ساختمان در
                        صفحه‌ی یک گوگل): دارد
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        سی-آر-ام (فرآیندهای فروش): دارد
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        کارگاه‌های آموزشی: ۲ بار (کارگاه/نفر) به مدت ۱۶ ساعت{" "}
                    </Typography>
                ),
            },
        ],
        subscriptionForNavigation: [
            AccountSubscriptionType.STANDARD,
            AccountSubscriptionType.ENHANCED,
        ],
    },
    {
        title: "تابان",
        type: AccountSubscriptionType.ULTIMATE,
        pricesInToman: { 3: 15_000_000, 6: 20_000_000, 12: 25_000_000 },
        imageUrl: SubscriptionTypeProperties.ULTIMATE.imageUrl,
        totalProjectCount: "۲۶٬۵۰۰+ پروژه تا آبان",
        titleColor: "#e2ac2e",
        exclusiveProjectCount: "۲,۰۰۰+ پروژه",
        definition: "برگ برنده: پروژه‌های خاص و بیشتر",
        description: `
                            پیشرو که باشید، «تابان» می‌شوید، یعنی می‌درخشید و با
                            کار و عملکردتان دیگران را هم روشن و نورانی می‌کنید.
                            هرآنچه که اشتراک‌های دیگر دارند که جای خود، آنچه
                            دیگران ندارند، اینجاست: پروژه‌های خاص و انحصاری تا
                            بازار را بسازید و برگ برنده داشته باشید. زمان فراتر
                            از طلاست و دسترسی به اطلاعات بیشتر، با کیفیت و خاص،
                            دارایی به ارزش الماس است. حالا اگر هردوی این‌ها را
                            داشته باشید، نه تنها بازار در دستان شماست، بلکه
                            می‌توانید بازار خودتان را بسازید. این یعنی با دردسر
                            کمتر، فروش بیشتر. با این اشتراک، برگ برنده در دستان
                            شماست، پس پیش بتازید و پرچم‌دار شوید.
                       `,
        properties: [
            {
                icon: "done",
                text: (
                    <>
                        <Typography color="text.secondary">
                            متراژ زمین:
                        </Typography>
                        <Typography
                            color="text.primary"
                            sx={{ fontWeight: 1000 }}
                        >
                            تا ۷۰۰ متر
                        </Typography>
                    </>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        کاربری: مسکونی، مسکونی تجاری و اداری تجاری
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        زمان دسترسی به پروژه‌ها: بلافاصله پس از گردآوری و
                        به‌روزرسانی
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        بازدید هفتگی پروژه‌ها:
                        <Typography
                            component={"span"}
                            color="text.primary"
                            sx={{ fontWeight: "1000" }}
                        >
                            {" "}
                            ۵۰۰ پروژه
                        </Typography>
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        شهرهای حومه:
                        <Typography
                            component={"span"}
                            color="text.primary"
                            sx={{ fontWeight: "1000" }}
                        >
                            {" "}
                            دارد
                        </Typography>
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        پنل تبلیغات و پیامک: &nbsp;
                        <Typography
                            component={"span"}
                            color="text.primary"
                            sx={{ fontWeight: "1000" }}
                        >
                            ۱۵۰۰ شارژ رایگان در اشتراک یک ساله
                        </Typography>
                        &nbsp; (شارژ بیشتر باید جداگانه خریداری شود)
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        سرشماره‌ی خدماتی برای پنل پیامک: &nbsp;
                        <Typography
                            component={"span"}
                            color="text.primary"
                            sx={{ fontWeight: "1000" }}
                        >
                            ۲۰ روز رایگان در اشتراک یک ساله
                        </Typography>
                        &nbsp; (بیشتر باید جداگانه خریداری شود)
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        سازه‌افزار (تبلیغ در وب‌سایت تبلیغاتی صنعت ساختمان در
                        صفحه‌ی یک گوگل): دارد
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        سی-آر-ام (فرآیندهای فروش): دارد
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        کارگاه‌های آموزشی:{" "}
                        <Typography
                            component={"span"}
                            color="text.primary"
                            sx={{ fontWeight: "1000" }}
                        >
                            ۴ بار (کارگاه/نفر) به مدت ۳۲ ساعت{" "}
                        </Typography>
                    </Typography>
                ),
            },
        ],
        subscriptionForNavigation: [
            AccountSubscriptionType.STANDARD,
            AccountSubscriptionType.ENHANCED,
            AccountSubscriptionType.ULTIMATE,
        ],
    },
    {
        title: "تابان پلاس",
        type: AccountSubscriptionType.ULTIMATE_PLUS,
        pricesInToman: { 3: 18_000_000, 6: 24_000_000, 12: 30_000_000 },
        imageUrl: SubscriptionTypeProperties.ULTIMATE_PLUS.imageUrl,
        totalProjectCount: "۳۰٬۰۰۰+ پروژه تا آبان",
        titleColor: "#36445e",
        exclusiveProjectCount: "۲,۵۰۰+ پروژه",
        definition: "بازار در دستان شما",
        description: `
                            «تابان پلاس» دسترسی بدون محدودیت و نهایت قدرت و تسلط
                            است. این اشتراک برای آنهایی است که می‌خواهند بازی را
                            عوض کنند و نبض بازار را به دست بگیرند. دسترسی به
                            بیشترین تعداد پروژه‌ها، بالاترین متراژها، خاص‌ترین
                            اطلاعات ممکن و فرصت‌های ناب؛ سریع، دقیق و جامع. با
                            تابان پلاس فروشنده‌ای به وسعت تهران و پروژه‌های خیلی
                            بزرگ باشید. پروژه‌های VIP و مشاوره‌های اختصاصی
                            مارکتینگ و فروش تنها در این اشتراک دردسترس هستند و
                            با داشتن این‌ها، شما می‌توانید رهبر بازار شوید و با
                            جذب مشتری‌های بزرگ با دردسر و هزینه‌های کمتر، سود
                            خود را چند برابر کنید. تابان پلاس یعنی حرکت به سوی
                            تسلط، نه رقابت. به تابان پلاس خوش آمدید: جایی که
                            مرزها معنا ندارند.
                       `,
        properties: [
            {
                icon: "done",
                text: (
                    <>
                        <Typography color="text.secondary">
                            متراژ زمین:
                        </Typography>
                        <Typography
                            color="text.primary"
                            sx={{ fontWeight: 1000 }}
                        >
                            بدون محدودیت
                        </Typography>
                    </>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        کاربری: مسکونی، مسکونی تجاری و اداری تجاری
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        زمان دسترسی به پروژه‌ها: بلافاصله پس از گردآوری و
                        به‌روزرسانی
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        بازدید هفتگی پروژه‌ها:
                        <Typography
                            component={"span"}
                            color="text.primary"
                            sx={{ fontWeight: "1000" }}
                        >
                            {" "}
                            ۵۰۰ پروژه
                        </Typography>
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        شهرهای حومه:
                        <Typography
                            component={"span"}
                            color="text.primary"
                            sx={{ fontWeight: "1000" }}
                        >
                            {" "}
                            دارد
                        </Typography>
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        پنل تبلیغات و پیامک: &nbsp;
                        <Typography
                            component={"span"}
                            color="text.primary"
                            sx={{ fontWeight: "1000" }}
                        >
                            ۳۰۰۰ شارژ رایگان در اشتراک یک ساله
                        </Typography>
                        &nbsp; (شارژ بیشتر باید جداگانه خریداری شود)
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        سرشماره‌ی خدماتی برای پنل پیامک: &nbsp;
                        <Typography
                            component={"span"}
                            color="text.primary"
                            sx={{ fontWeight: "1000" }}
                        >
                            ۳۰ روز رایگان در اشتراک یک ساله
                        </Typography>
                        &nbsp; (بیشتر باید جداگانه خریداری شود)
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        سازه‌افزار (تبلیغ در وب‌سایت تبلیغاتی صنعت ساختمان در
                        صفحه‌ی یک گوگل): دارد
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        سی-آر-ام (فرآیندهای فروش): دارد
                    </Typography>
                ),
            },
            {
                icon: "done",
                text: (
                    <Typography color="text.secondary">
                        کارگاه‌های آموزشی:{" "}
                        <Typography
                            component={"span"}
                            color="text.primary"
                            sx={{ fontWeight: "1000" }}
                        >
                            ۸ بار (کارگاه/نفر) به مدت ۶۴ ساعت
                        </Typography>
                    </Typography>
                ),
            },
        ],
        subscriptionForNavigation: [],
    },
];
